<template>
  <header
    class="header"
    :class="{
      'header--scrolled': isScrolled,
      'header--transparent': isTransparent && !isScrolled
    }"
  >
    <div class="container">
      <div class="header__content">
        <!-- Dynamic Logo -->
        <router-link to="/" class="header__logo">
          <img src="/Inver_Sun-removebg-preview.png" alt="Inversun" class="header__logo-img">
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="header__nav">
          <router-link to="/" class="header__nav-link">{{ cms.header.nav_home || 'Home' }}</router-link>
          <router-link to="/shop" class="header__nav-link">{{ cms.header.nav_shop || 'Shop' }}</router-link>

          <router-link to="/products" class="header__nav-link">{{ cms.header.nav_products || 'Products' }}</router-link>

          <router-link to="/about" class="header__nav-link">{{ cms.header.nav_about || 'About' }}</router-link>
          <router-link to="/services" class="header__nav-link">{{ cms.header.nav_services || 'Service Center' }}</router-link>
          <router-link to="/distributors" class="header__nav-link">{{ cms.header.nav_distributors || 'Distributors' }}</router-link>
          <router-link to="/support" class="header__nav-link">{{ cms.header.nav_support || 'Support' }}</router-link>
          <router-link to="/contact" class="header__nav-link">{{ cms.header.nav_contact || 'Contact' }}</router-link>
        </nav>

        <!-- Actions -->
        <div class="header__actions">
          <button
            class="header__cart-toggle"
            @click="cartStore.toggleCart"
            :title="`Open cart (${cartStore.itemCount})`"
          >
            <ShoppingCart :size="20" />
            <span v-if="cartStore.itemCount > 0" class="header__cart-count">
              {{ cartStore.itemCount }}
            </span>
          </button>

          <!-- Theme Toggle -->
          <button
            class="header__theme-toggle"
            @click="toggleDarkMode"
            :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          >
            <Moon v-if="!isDark" :size="20" />
            <Sun v-else :size="20" />
          </button>

          <!-- Auth Button -->
          <router-link
            v-if="!authStore.isAuthenticated"
            to="/login"
            class="header__auth-icon"
            title="Sign in"
          >
            <User :size="20" />
          </router-link>

          <!-- User Menu (when authenticated) -->
          <div v-else class="header__user-menu">
            <button
              class="header__user-icon"
              @click="showUserMenu = !showUserMenu"
              aria-haspopup="true"
              :aria-expanded="showUserMenu"
              :title="authStore.userName"
            >
              <User :size="20" />
            </button>
            <Transition name="dropdown">
              <div v-if="showUserMenu" class="dropdown-menu dropdown-menu--right">
                <router-link to="/account" class="dropdown-item" @click="showUserMenu = false">
                  <User :size="16" />
                  My Account
                </router-link>
                <router-link to="/account/orders" class="dropdown-item" @click="showUserMenu = false">
                  <ShoppingBag :size="16" />
                  My Orders
                </router-link>
                <router-link to="/account/wishlist" class="dropdown-item" @click="showUserMenu = false">
                  <Heart :size="16" />
                  My Wishlist
                </router-link>
                <div class="dropdown-divider"></div>
                <button class="dropdown-item dropdown-item--danger" @click="handleLogout">
                  <LogOut :size="16" />
                  Sign out
                </button>
              </div>
            </Transition>
          </div>

          <!-- Mobile Menu Toggle -->
          <button class="header__mobile-toggle" @click="mobileMenuOpen = true">
            <Menu :size="24" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <Teleport to="body">
      <Transition name="slide">
        <div v-if="mobileMenuOpen" class="mobile-menu">
          <div class="mobile-menu__header">
            <span class="mobile-menu__title">Menu</span>
            <button class="mobile-menu__close" @click="mobileMenuOpen = false">
              <X :size="24" />
            </button>
          </div>

          <nav class="mobile-menu__nav">
            <router-link to="/" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <Home :size="20" />
              Home
            </router-link>
            <router-link to="/shop" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <ShoppingBag :size="20" />
              Shop
            </router-link>
            
            <router-link to="/products" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <Package :size="20" />
              Products
            </router-link>
            
            <router-link to="/cart" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <ShoppingCart :size="20" />
              Cart
              <span v-if="cartStore.itemCount > 0" class="mobile-menu__link-count">
                {{ cartStore.itemCount }}
              </span>
            </router-link>
            <router-link to="/about" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <Info :size="20" />
              About
            </router-link>
            <router-link to="/services" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <Wrench :size="20" />
              Service Center
            </router-link>
            <router-link to="/distributors" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <MapPin :size="20" />
              Distributors
            </router-link>
            <router-link to="/support" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <HeadphonesIcon :size="20" />
              Support
            </router-link>
            <router-link to="/contact" class="mobile-menu__link" @click="mobileMenuOpen = false">
              <MessageCircle :size="20" />
              Contact
            </router-link>
            
            <!-- Account Section -->
            <div class="mobile-menu__section" v-if="!authStore.isAuthenticated">
              <div class="mobile-menu__section-title">Account</div>
              <router-link to="/login" class="mobile-menu__link" @click="mobileMenuOpen = false">
                <User :size="20" />
                Sign in
              </router-link>
              <router-link to="/register" class="mobile-menu__link" @click="mobileMenuOpen = false">
                <User :size="20" />
                Register
              </router-link>
            </div>
            
            <div class="mobile-menu__section" v-else>
              <div class="mobile-menu__section-title">My Account</div>
              <router-link to="/account" class="mobile-menu__link" @click="mobileMenuOpen = false">
                <User :size="20" />
                Account Overview
              </router-link>
              <router-link to="/account/orders" class="mobile-menu__link" @click="mobileMenuOpen = false">
                <ShoppingBag :size="20" />
                My Orders
              </router-link>
              <router-link to="/account/wishlist" class="mobile-menu__link" @click="mobileMenuOpen = false">
                <Heart :size="20" />
                My Wishlist
              </router-link>
              <button class="mobile-menu__link mobile-menu__link--danger" @click="handleLogout">
                <LogOut :size="20" />
                Sign out
              </button>
            </div>
          </nav>

          <div class="mobile-menu__footer">
            <button class="mobile-menu__theme-btn" @click="toggleDarkMode">
              <Moon v-if="!isDark" :size="18" />
              <Sun v-else :size="18" />
              {{ isDark ? 'Light Mode' : 'Dark Mode' }}
            </button>
          </div>
        </div>
      </Transition>
      <Transition name="fade">
        <div v-if="mobileMenuOpen" class="mobile-menu__overlay" @click="mobileMenuOpen = false" />
      </Transition>
    </Teleport>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Menu, X, Moon, Sun, Home, ShoppingBag, ShoppingCart, MessageCircle, Info, Wrench, MapPin, HeadphonesIcon, Package, User, Heart, LogOut } from 'lucide-vue-next'
import { useDarkMode } from '@/composables/useDarkMode'
import { useCms } from '@/composables/useCms'
import { useCartStore } from '@/stores/cart'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const { isDark, toggleDarkMode } = useDarkMode()
const cms = useCms()
const cartStore = useCartStore()
const authStore = useAuthStore()

const isScrolled = ref(false)
const mobileMenuOpen = ref(false)
const showUserMenu = ref(false)

const handleLogout = async () => {
  showUserMenu.value = false
  await authStore.logout()
  router.push('/')
}

// Check if current page should have transparent header
const isTransparent = computed(() => {
  return route.path === '/' || route.path === '/contact'
})

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

onMounted(async () => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-700: #1D4ED8;
$gold: #F59E0B;
$accent-cyan: #06B6D4;
$danger: #EF4444;
$dark-900: #030712;
$dark-800: #0A0F1C;

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--glass-bg-solid);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border-bottom: 1px solid var(--border-light);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  &--transparent {
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-bottom-color: transparent;

    .header__logo-img {
      filter: brightness(0) saturate(100%) invert(100%);
    }

    .header__nav-link {
      color: rgba(255, 255, 255, 0.85);

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }

      &.router-link-active {
        background: rgba(255, 255, 255, 0.15);
        color: white;
      }
    }

    .header__theme-toggle,
    .header__cart-toggle,
    .header__mobile-toggle {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.15);
      color: white;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }

  &--scrolled {
    background: var(--glass-bg-solid);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    border-bottom-color: var(--border-light);

    .header__logo-img {
      filter: none;
    }

    .header__nav-link {
      color: var(--text-primary);

      &:hover {
        background: rgba($primary, 0.08);
        color: $primary;
      }

      &.router-link-active {
        background: rgba($primary, 0.1);
        color: $primary;
      }
    }

    .header__theme-toggle,
    .header__cart-toggle,
    .header__mobile-toggle {
      background: var(--background-secondary);
      border-color: var(--border);
      color: var(--text-primary);

      &:hover {
        background: var(--background-tertiary);
        border-color: $primary;
        color: $primary;
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 80px;
  }

  &__logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  &__logo-img {
    height: 250px;
    width: auto;
    object-fit: contain;
    transition: all 0.3s ease;
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    @media (max-width: 768px) {
      display: none;
    }
  }

  &__nav-link {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-family: 'Outfit', sans-serif;
    font-weight: 500;
    color: var(--text-primary);
    text-decoration: none;
    padding: 0.65rem 1.15rem;
    border-radius: 12px;
    transition: all 0.25s ease;
    position: relative;

    &:hover {
      background: rgba($primary, 0.08);
      color: $primary;
    }

    &.router-link-active {
      color: $primary;
      background: rgba($primary, 0.1);

      &::after {
        content: '';
        position: absolute;
        bottom: 6px;
        left: 50%;
        transform: translateX(-50%);
        width: 20px;
        height: 3px;
        background: linear-gradient(90deg, $primary, $gold);
        border-radius: 2px;
      }
    }

    svg {
      transition: transform 0.3s ease;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &--button {
      border: none;
      background: transparent;
      font-size: inherit;
      line-height: inherit;
      cursor: pointer;
    }
  }

  &__nav-dropdown {
    position: relative;
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    min-width: 220px;
    background: var(--background);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    z-index: 1000;
    padding: 0.5rem 0;

    &--right {
      left: auto;
      right: 0;
    }
  }

  .dropdown-divider {
    height: 1px;
    background: var(--border);
    margin: 0.5rem 0;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    color: var(--text-secondary);
    text-decoration: none;
    font-size: 0.95rem;
    font-weight: 500;
    transition: all 0.2s ease;

    svg {
      color: var(--text-muted);
      transition: color 0.2s;
    }

    &:hover {
      background: rgba($primary, 0.08);
      color: $primary;

      svg {
        color: $primary;
      }
    }

    &.router-link-active {
      background: linear-gradient(135deg, rgba($primary, 0.1), rgba($gold, 0.05));
      color: $primary;

      svg {
        color: $gold;
      }
    }

    &--danger {
      width: 100%;
      background: none;
      border: none;
      cursor: pointer;
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      text-align: left;

      &:hover {
        background: rgba($danger, 0.08);
        color: $danger;

        svg {
          color: $danger;
        }
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__theme-toggle {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--background-secondary);
    border: 1px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
    color: var(--text-primary);
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
      background: var(--background-tertiary);
      border-color: $gold;
      color: $gold;
      transform: rotate(180deg);
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  &__cart-toggle {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--background-secondary);
    border: 1px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
    color: var(--text-primary);
    position: relative;
    transition: all 0.25s ease;

    &:hover {
      background: var(--background-tertiary);
      border-color: $primary;
      color: $primary;
      transform: translateY(-1px);
    }
  }

  &__cart-count {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.35rem;
    border-radius: 999px;
    background: linear-gradient(135deg, $gold, $primary);
    color: white;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    border: 2px solid var(--background);
  }

  &__auth-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, $primary, $primary-700);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    color: white;
    text-decoration: none;
    transition: all 0.25s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba($primary, 0.4);
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  &__user-menu {
    position: relative;
  }

  &__user-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba($gold, 0.15), rgba($gold, 0.05));
    border: 1px solid rgba($gold, 0.3);
    border-radius: 12px;
    cursor: pointer;
    color: $gold;
    transition: all 0.25s ease;

    &:hover {
      background: linear-gradient(135deg, rgba($gold, 0.25), rgba($gold, 0.1));
      border-color: $gold;
      transform: translateY(-1px);
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  &__mobile-toggle {
    display: none;
    width: 48px;
    height: 48px;
    align-items: center;
    justify-content: center;
    background: var(--background-secondary);
    border: 1px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
    color: var(--text-primary);
    transition: all 0.2s;

    &:hover {
      background: var(--background-tertiary);
    }

    @media (max-width: 768px) {
      display: flex;
    }
  }
}

// Mobile Menu
.mobile-menu {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 90%;
  background: var(--background);
  z-index: 1001;
  padding: 1.5rem;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;

  &__overlay {
    position: fixed;
    inset: 0;
    background: rgba($dark-900, 0.7);
    backdrop-filter: blur(8px);
    z-index: 1000;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--border);
  }

  &__title {
    font-family: 'Outfit', sans-serif;
    font-size: 1.25rem;
    font-weight: 700;
    background: linear-gradient(135deg, $primary, $gold);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &__close {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--background-secondary);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    color: var(--text-muted);
    transition: all 0.3s;

    &:hover {
      background: $primary;
      color: white;
      transform: rotate(90deg);
    }
  }

  &__nav {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    overflow-y: auto;
  }

  &__section {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--border);
  }

  &__section-title {
    font-family: 'Outfit', sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.5rem 1.25rem;
    margin-bottom: 0.25rem;
  }

    &__link {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 1.25rem;
      font-family: 'Outfit', sans-serif;
      font-weight: 600;
      color: var(--text-primary);
      text-decoration: none;
      border-radius: 14px;
      transition: all 0.25s ease;
      background: none;
      border: none;
      width: 100%;
      cursor: pointer;
      text-align: left;

      svg {
        color: var(--text-muted);
        transition: color 0.25s ease;
      }

      &:hover {
        background: rgba($primary, 0.1);
        color: $primary;
        transform: translateX(4px);

        svg {
          color: $primary;
        }
      }

      &.router-link-active {
        background: linear-gradient(135deg, rgba($primary, 0.15), rgba($gold, 0.1));
        color: $primary;

        svg {
          color: $gold;
        }
      }

      &--sub {
        padding-left: 2.5rem;
        font-size: 0.95rem;
        font-weight: 500;

        svg {
          width: 18px;
          height: 18px;
        }
      }

      &--danger {
        &:hover {
          background: rgba($danger, 0.1);
          color: $danger;

          svg {
            color: $danger;
          }
        }
      }
    }

  &__link-count {
    margin-left: auto;
    min-width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.45rem;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba($primary, 0.2), rgba($gold, 0.2));
    color: $primary;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
  }

  &__footer {
    padding-top: 1.5rem;
    border-top: 1px solid var(--border);
  }

  &__theme-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 1rem;
    background: var(--background-secondary);
    border: 1px solid var(--border);
    border-radius: 12px;
    font-family: 'Outfit', sans-serif;
    font-weight: 600;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: var(--background-tertiary);
      border-color: $gold;
      color: $gold;
    }
  }
}

// Transitions
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
